import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, map, tap } from 'rxjs';
import { ApiService } from './api-service';
import { Settings, SettingFlag, SettingValue } from '../models/settings.model';
import { AppSettings } from '../models/app-settings.model';
import { DocumentCategory } from '../models/documents.model';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private api = inject(ApiService);

  private readonly appSettingsSubject = new BehaviorSubject<AppSettings[] | null>(null);
  readonly appSettings$ = this.appSettingsSubject.asObservable();

  private readonly categoriesSubject = new BehaviorSubject<DocumentCategory[] | null>(null);

  loadAllSettings(): Observable<AppSettings[]> {
    const cached = this.appSettingsSubject.getValue();
    if (cached) {
      return of(cached);
    }
    return this.api.get<AppSettings[]>('/settings/app/all').pipe(
      tap(settings => this.appSettingsSubject.next(settings))
    );
  }

  clearSettingsCache(): void {
    this.appSettingsSubject.next(null);
  }

  getPdfFormatSetting(): Observable<SettingFlag> {
    return this.api.get<SettingFlag>('/settings/format/pdf/enabled');
  }

  getExcelFormatSetting(): Observable<SettingFlag> {
    return this.api.get<SettingFlag>('/settings/format/excel/enabled');
  }

  getDocFormatSetting(): Observable<SettingFlag> {
    return this.api.get<SettingFlag>('/settings/format/doc/enabled');
  }

  getMarkdownFormatSetting(): Observable<SettingFlag> {
    return this.api.get<SettingFlag>('/settings/format/md/enabled');
  }

  getPowerpointFormatSetting(): Observable<SettingFlag> {
    return this.api.get<SettingFlag>('/settings/format/pptx/enabled');
  }

  getDeletionEnabled(): Observable<SettingFlag> {
    return this.api.get<SettingFlag>('/settings/deletion/enabled');
  }

  getDocumentLimit(): Observable<SettingValue> {
    return this.api.get<SettingValue>('/settings/dimension/limit');
  }

  postFormatSetting(params: Settings): Observable<any> {
    return this.api.post('/settings/format', params);
  }

  postDocumentLimitSetting(params: Settings): Observable<any> {
    return this.api.post('/settings/dimension/limit', params);
  }

  postDeletionEnabledSetting(params: Settings): Observable<any> {
    return this.api.post('/settings/deletion/enabled', params);
  }

  getCategories(): Observable<DocumentCategory[]> {
    const cached = this.categoriesSubject.getValue();
    if (cached) {
      return of(cached);
    }
    return this.api.get<DocumentCategory[]>('/settings/categories').pipe(
      map(categories => categories.map(c => ({ ...c, value: c.value.toUpperCase() }))),
      tap(categories => this.categoriesSubject.next(categories))
    );
  }

  clearCategoriesCache(): void {
    this.categoriesSubject.next(null);
  }
}
